<script>
import {
  iBadge,
  iDropzone,
  iDropzoneTypes,
  iIcon,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iDrawer,
  iTextInput,
  iSelect,
  showToast,
} from '@inscribe/signal';
import { showDuplicateUploadToast } from '@/utils/notifications';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import TagsSelector from '@/V2/components/TagsSelector.vue';

export default {
  components: {
    iBadge,
    iDropzone,
    iIcon,
    iButton,
    iDrawer,
    iTextInput,
    iSelect,
    TagsSelector,
  },
  data() {
    return {
      iDropzoneTypes,
      customerName: '',
      showCompanySection: false,
      showPersonsSection: false,
      companyData: {
        name: '',
        structure: '',
        address: {
          line_1: '',
          line_2: '',
          city: '',
          post_code: '',
          state: '',
          country: '',
        },
        phone_number: '',
        source_of_funds_claim: '',
      },
      structureOptions: [
        { value: 'SOLE_PROPRIETORSHIP', text: 'Sole Proprietorship' },
        { value: 'PARTNERSHIP', text: 'Partnership' },
        { value: 'CORPORATION', text: 'Corporation' },
        {
          value: 'LIMITED_LIABILITY_COMPANY',
          text: 'Limited Liability Company',
        },
        { value: 'PRIVATE_LIMITED_COMPANY', text: 'Private Limited Company' },
        { value: 'NON_PROFIT', text: 'Non Profit' },
      ],
      personsData: [
        {
          first_name: '',
          last_name: '',
        },
      ],
      errors: [],
      isLoading: false,
      files: [],
      selectedTags: [],
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      isDrawerVisible: false,
      showValidation: false,
    };
  },
  computed: {
    ...mapGetters({
      getTagsList: 'getTagsList',
      canViewBusinessComplianceAgent: 'canViewBusinessComplianceAgent',
    }),
    tags() {
      return this.getTagsList.map((tagObject) => {
        return {
          label: tagObject.value,
        };
      });
    },
    showCompanyButton() {
      return this.canViewBusinessComplianceAgent && !this.showCompanySection;
    },
    showPersonsButton() {
      return this.canViewBusinessComplianceAgent && !this.showPersonsSection;
    },
    customerNameValid() {
      return this.customerName?.length > 2;
    },
    companyDataValid() {
      if (!this.showCompanySection) return true;
      return this.companyData.name?.length > 0;
    },
    personsDataValid() {
      if (!this.showPersonsSection) return true;
      return this.personsData.every(
        (person) =>
          person.first_name?.length > 0 && person.last_name?.length > 0,
      );
    },
    formValid() {
      if (!this.customerNameValid) return false;
      if (this.showCompanySection && !this.companyDataValid) return false;
      if (this.showPersonsSection && !this.personsDataValid) return false;
      return true;
    },
    hasCustomerNameError() {
      return this.showValidation && !this.customerNameValid;
    },
    hasCompanyNameError() {
      return (
        this.showValidation && this.showCompanySection && !this.companyData.name
      );
    },
    hasPersonsError() {
      return (
        this.showValidation && this.showPersonsSection && !this.personsDataValid
      );
    },
  },
  mounted() {
    this.$store.commit('setSelectedCustomerId', undefined);
    this.isDrawerVisible = true;
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    ...mapActions(['createNewCustomer', 'uploadCustomerFiles']),
    ...mapMutations(['addTags']),
    handleKeyDown(event) {
      if (event.key === 'Enter' && this.isDrawerVisible && !this.isLoading) {
        this.createCustomer();
      }
    },
    onFilesSelected(files) {
      this.files = [...this.files, ...files];
    },
    onTagsSelected(tags) {
      this.selectedTags = [...tags];
    },
    onTagCreateButtonClicked(tag) {
      this.addTags([
        {
          value: tag,
        },
      ]);
      this.selectedTags.push(tag);
    },
    deleteTag(tag) {
      if (!this.disabled) {
        const tagIndex = this.selectedTags.findIndex(
          (selectedTag) => selectedTag === tag,
        );
        if (tagIndex > -1) {
          this.selectedTags.splice(tagIndex, 1);
        }
      }
    },
    deleteFile(file) {
      const foundIndex = this.files.findIndex((f) => f === file);
      if (foundIndex > -1) {
        this.files.splice(foundIndex, 1);
      }
    },
    loadCustomerRoute() {
      this.$router.push({ name: 'customers-view', query: this.$route.query });
    },
    onClose() {
      if (!this.isLoading) {
        this.isDrawerVisible = false;
        this.showValidation = false;
        window.removeEventListener('keydown', this.handleKeyDown);
        this.loadCustomerRoute();
      }
    },
    onCompanyFieldChange(field, value) {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        this.companyData[parent][child] = value;
      } else {
        this.companyData[field] = value;
      }
    },
    onPersonFieldChange(index, field, value) {
      this.personsData[index][field] = value;
    },
    addPerson() {
      this.personsData.push({
        first_name: '',
        last_name: '',
      });
    },
    removePerson(index) {
      if (this.personsData.length > 1) {
        this.personsData.splice(index, 1);
      }
    },
    createCustomer() {
      this.showValidation = true;
      if (!this.formValid) return;

      this.isLoading = true;
      const customerData = {
        name: this.customerName,
        ...(this.showCompanySection && { company: this.companyData }),
        ...(this.showPersonsSection && { persons: this.personsData }),
      };

      this.createNewCustomer(customerData).then(
        async (response) => {
          const customerData = response.data;
          this.$store.commit('addCustomer', response.data);
          showToast({
            text: 'Customer successfully created. You can now add documents to this customer and start checking for fraud.',
          });
          if (this.files.length) {
            await this.uploadFiles(customerData).catch(() => {});
          }
          this.isDrawerVisible = false;
          this.$router.push({
            name: 'customer-overview',
            params: { customer_id: response.data.id },
          });
          this.isLoading = false;
        },
        (error) => {
          showToast({
            text: error?.response?.data?.message || '',
            isError: true,
          });
          this.isLoading = false;
        },
      );
    },
    uploadFiles(customerData) {
      return new Promise((resolve, reject) => {
        this.uploadCustomerFiles({
          customerId: customerData.id,
          tags: this.selectedTags,
          files: this.files,
        }).then(
          function (response) {
            showToast({
              text: 'Your documents have been uploaded and are now being processed.',
            });
            resolve(response);
          },
          function (error) {
            if (error.status == 409) {
              showDuplicateUploadToast(error.response.data.duplicates);
            } else {
              showToast({
                text: error?.response?.data?.message || '',
                isError: true,
              });
            }
            reject(error);
          },
        );
      });
    },
  },
};
</script>

<template>
  <div class="new-customer">
    <i-drawer
      :visible="isDrawerVisible"
      title="New Customer"
      subtitle="Create a new customer"
      side="right"
      animation="slide"
      @hide-drawer="onClose"
    >
      <div
        class="d-flex flex-column i-gap-8 i-px-4"
        data-testid="new-customer-drawer"
      >
        <i-text-input
          data-testid="new-customer-name-input"
          label="Customer Name"
          :model-value="customerName"
          placeholder="Customer #1234"
          :has-error="hasCustomerNameError"
          :show-hint-text="hasCustomerNameError"
          hint-text="Customer name must be at least 3 characters"
          data-dd-action-name="customers-view-new-customer-name-input"
          @update:model-value="customerName = $event"
        />

        <!-- files list -->
        <div
          v-if="files.length > 0"
          class="files-list d-flex flex-column i-gap-2"
        >
          <div
            v-for="(file, index) in Array.from(files)"
            :key="index"
            class="file-item d-flex flex-row align-items-center i-radius-sm i-px-6 i-py-5 i-text-subheading-2 i-text-neutral-120"
            data-dd-action-name="customers-view-new-customer-file-item"
          >
            <i-icon
              symbol="small-file"
              :size="16"
              class="file-icon i-mr-6"
            ></i-icon>
            <div class="name flex-grow-1">{{ file.name }}</div>
            <i-button
              label="Remove"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              :size="iButtonSizes.SMALL"
              :disabled="isLoading"
              @button-click="deleteFile(file)"
              data-dd-action-name="customers-view-new-customer-file-item-remove-button"
            />
          </div>
        </div>

        <i-dropzone
          title="Choose file or drag and drop"
          sub-title="PDFs, JPEGs and PNGs accepted"
          :accepted-extensions="['pdf', 'jpg', 'jpeg', 'png']"
          :disabled="isLoading"
          @files-selected="onFilesSelected"
          data-dd-action-name="customers-view-new-customer-choose-files-dropzone"
        ></i-dropzone>

        <div class="d-flex flex-column i-gap-6">
          <tags-selector
            v-if="files.length > 0"
            label="Tag these documents"
            title="Select the tags"
            data-testid="assign-tags-filter"
            trigger-locator=".i-badge"
            :pre-selected-tags="selectedTags"
            @input="onTagsSelected"
            @create-button-clicked="onTagCreateButtonClicked"
          >
          </tags-selector>

          <div
            v-if="selectedTags.length"
            class="tags-list d-flex flex-row i-gap-3"
          >
            <i-badge
              v-for="(tag, index) in selectedTags"
              :key="index"
              :label="tag"
              :has-trailing-icon="true"
              :interactable="true"
              :outline="true"
              icon="small-delete-1"
              @right-icon-click="deleteTag(tag)"
            >
            </i-badge>
          </div>
        </div>

        <!-- Company Information Button -->
        <div v-if="showCompanyButton">
          <i-button
            label="Add Company Information"
            :size="iButtonSizes.LARGE"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.OUTLINE"
            class="w-100"
            @button-click="
              () => {
                showCompanySection = true;
                showValidation = false;
              }
            "
          />
        </div>

        <!-- Company Information Form -->
        <div
          v-if="showCompanySection && canViewBusinessComplianceAgent"
          class="company-info d-flex flex-column i-gap-6"
        >
          <div class="d-flex align-items-center justify-content-between i-pb-4">
            <div class="i-text-subheading-2 i-text-neutral-120">
              Company Information
            </div>
            <i-button
              label="Remove"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              :size="iButtonSizes.SMALL"
              @button-click="
                () => {
                  showCompanySection = false;
                  showValidation = false;
                }
              "
            />
          </div>

          <i-text-input
            label="Company Name"
            :model-value="companyData.name"
            placeholder="Acme Inc"
            :has-error="hasCompanyNameError"
            :show-hint-text="hasCompanyNameError"
            hint-text="Company name is required"
            @update:model-value="(value) => onCompanyFieldChange('name', value)"
          />

          <i-select
            label="Structure"
            :options="structureOptions"
            :model-value="companyData.structure"
            placeholder="Select company structure"
            size="md"
            @option-selected="
              (value) => onCompanyFieldChange('structure', value)
            "
          />

          <div class="address-fields d-flex flex-column i-gap-4">
            <i-text-input
              label="Address"
              :model-value="companyData.address.line_1"
              placeholder="Line 1"
              @update:model-value="
                (value) => onCompanyFieldChange('address.line_1', value)
              "
            />

            <i-text-input
              :model-value="companyData.address.line_2"
              placeholder="Line 2"
              @update:model-value="
                (value) => onCompanyFieldChange('address.line_2', value)
              "
            />

            <div class="d-flex i-gap-4">
              <i-text-input
                class="flex-grow-1"
                :model-value="companyData.address.city"
                placeholder="City"
                @update:model-value="
                  (value) => onCompanyFieldChange('address.city', value)
                "
              />

              <i-text-input
                class="flex-grow-1"
                :model-value="companyData.address.state"
                placeholder="State"
                @update:model-value="
                  (value) => onCompanyFieldChange('address.state', value)
                "
              />
            </div>

            <div class="d-flex i-gap-4">
              <i-text-input
                class="flex-grow-1"
                :model-value="companyData.address.post_code"
                placeholder="Postal Code"
                @update:model-value="
                  (value) => onCompanyFieldChange('address.post_code', value)
                "
              />

              <i-text-input
                class="flex-grow-1"
                :model-value="companyData.address.country"
                placeholder="Country"
                @update:model-value="
                  (value) => onCompanyFieldChange('address.country', value)
                "
              />
            </div>
          </div>
          <i-text-input
            label="Phone Number"
            :model-value="companyData.phone_number"
            placeholder="+1 555 123 4567"
            @update:model-value="
              (value) => onCompanyFieldChange('phone_number', value)
            "
          />
          <i-text-input
            label="Source of Funds"
            :model-value="companyData.source_of_funds_claim"
            placeholder="e.g. Business Revenue, Investment Capital"
            @update:model-value="
              (value) => onCompanyFieldChange('source_of_funds_claim', value)
            "
          />
        </div>

        <!-- Persons Information Button -->
        <div v-if="showPersonsButton">
          <i-button
            label="Add Associated Persons"
            :size="iButtonSizes.LARGE"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.OUTLINE"
            class="w-100"
            @button-click="
              () => {
                showPersonsSection = true;
                showValidation = false;
              }
            "
          />
        </div>

        <!-- Persons Information Form -->
        <div
          v-if="showPersonsSection && canViewBusinessComplianceAgent"
          class="persons-info d-flex flex-column i-gap-6"
        >
          <div class="d-flex align-items-center justify-content-between i-pb-4">
            <div class="i-text-subheading-2 i-text-neutral-120">
              Associated Persons
            </div>
            <i-button
              label="Remove"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              :size="iButtonSizes.SMALL"
              @button-click="
                () => {
                  showPersonsSection = false;
                  showValidation = false;
                }
              "
            />
          </div>

          <div class="persons-list d-flex flex-column i-gap-6">
            <div
              v-for="(person, index) in personsData"
              :key="index"
              class="person-item d-flex flex-column i-gap-4"
            >
              <div class="d-flex i-gap-4 align-items-end">
                <i-text-input
                  class="flex-grow-1"
                  label="First Name"
                  :model-value="person.first_name"
                  placeholder="John"
                  :has-error="hasPersonsError && !person.first_name"
                  :show-hint-text="hasPersonsError && !person.first_name"
                  hint-text="First name is required"
                  @update:model-value="
                    (value) => onPersonFieldChange(index, 'first_name', value)
                  "
                />

                <i-text-input
                  class="flex-grow-1"
                  label="Last Name"
                  :model-value="person.last_name"
                  placeholder="Smith"
                  :has-error="hasPersonsError && !person.last_name"
                  :show-hint-text="hasPersonsError && !person.last_name"
                  hint-text="Last name is required"
                  @update:model-value="
                    (value) => onPersonFieldChange(index, 'last_name', value)
                  "
                />

                <i-button
                  v-if="personsData.length > 1"
                  :type="iButtonTypes.NEUTRAL"
                  :button-style="iButtonStyles.SOLID_LIGHT"
                  :size="iButtonSizes.LARGE"
                  left-icon="medium_delete_bin_1"
                  @button-click="removePerson(index)"
                />
              </div>
            </div>
          </div>

          <div class="d-flex">
            <i-button
              label="Add Person"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              :size="iButtonSizes.SMALL"
              @button-click="addPerson"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="d-flex i-gap-4 w-100">
          <i-button
            label="Cancel"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.OUTLINE"
            :size="iButtonSizes.LARGE"
            :disabled="isLoading"
            @button-click="onClose"
            class="flex-grow-1"
            data-dd-action-name="customers-view-new-customer-overlay-cancel-button"
          />
          <i-button
            label="Create"
            :type="iButtonTypes.PRIMARY"
            :button-style="iButtonStyles.SOLID_DARK"
            :size="iButtonSizes.LARGE"
            :loading="isLoading"
            :disabled="isLoading"
            v-read-only-check
            @button-click="createCustomer"
            class="flex-grow-1"
            data-testid="drawer-new-customer-create-button"
            data-dd-action-name="customers-view-new-customer-overlay-create-button"
          />
        </div>
      </template>
    </i-drawer>
  </div>
</template>

<style scoped lang="scss">
.new-customer {
  .files-list {
    max-height: 200px;
    overflow-y: auto;

    .file-item {
      background: $light-neutral-20;
    }
  }

  .tags-list {
    flex-wrap: wrap;
    max-height: 100px;
    overflow-y: auto;
    overflow-x: hidden;
  }
}

:deep(.i-toast) {
  z-index: 9999 !important;
}
</style>
